import {
    FormProvider,
    SubmitHandler,
    useForm,
    useFormContext,
    useFormState,
} from 'react-hook-form';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import {
    Alert,
    AlertTitle,
    Box,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    Divider,
    Grid2,
    IconButton,
    Stack,
    Tooltip,
    Typography,
} from '@mui/material';
import FormInputText from '@components/forms/FormInputText';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { getSpoofUrl } from '@modules/api';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { getUserByEmail } from '@modules/supabase/users';
import { User } from '@type/db-types';
import WrestlerNameForm from '@components/AddWrestler/WrestlerNameForm';
import { debounce } from 'lodash';
import { breadcrumb } from '@utils/logging';
import useModalControl from '@hooks/useModalControl';
import UploadFileIcon from '@mui/icons-material/UploadFile';

interface Props {
    onClose: () => void;
    showModal: boolean;
}

export interface SpoofUserForm {
    firstName?: string;
    lastName?: string;
    email: string;
    existingUser: boolean;
}

const getDefaultValues = (): SpoofUserForm => ({
    email: '',
    firstName: '',
    lastName: '',
    existingUser: true,
});

export default function SpoofModal({ onClose, showModal }: Props) {
    const [url, setUrl] = useState<string | null>(null);
    const { setShowTeamImportModal } = useModalControl();

    const formMethods = useForm<SpoofUserForm>({
        defaultValues: getDefaultValues(),
        mode: 'all',
        resolver: yupResolver(emailFormSchema),
    });

    const onSubmit: SubmitHandler<SpoofUserForm> = useCallback((values) => {
        getSpoofUrl(values).then(setUrl);
    }, []);

    const [currentEmail] = formMethods.watch(['email']);

    useEffect(() => {
        setUrl(null);
    }, [currentEmail.length]);

    const close = useCallback(() => {
        onClose();
        setUrl(null);
        formMethods.reset(getDefaultValues());
    }, [formMethods, onClose]);

    return (
        <FormProvider {...formMethods}>
            <form>
                <Dialog fullWidth maxWidth="sm" open={showModal} onClose={close}>
                    <DialogTitle
                        sx={{
                            margin: '1rem',
                            paddingBottom: 0,
                            textAlign: 'center',
                        }}
                    >
                        Bulk Import Teams:{' '}
                        <IconButton color="primary" onClick={() => setShowTeamImportModal(true)}>
                            <UploadFileIcon />
                        </IconButton>
                    </DialogTitle>
                    <Divider />
                    <DialogTitle
                        sx={{
                            marginTop: '1rem',
                            paddingBottom: 0,
                            textAlign: 'center',
                        }}
                    >
                        Enter email to spoof:
                    </DialogTitle>
                    <DialogContent sx={{ margin: '1rem' }}>
                        <FormInputText
                            name="email"
                            label="Email"
                            textFieldProps={{
                                slotProps: {
                                    input: {
                                        endAdornment: <SubmitButton onSubmit={onSubmit} />,
                                    },
                                },
                            }}
                        />
                        {url ? (
                            <Tooltip title="URL Copied to clipboard! Copy again?">
                                <Stack spacing={1}>
                                    <Box display="flex" alignItems="center">
                                        <Alert severity="success">
                                            Success! Copy URL to incognito tab
                                        </Alert>
                                        <IconButton
                                            sx={{ marginLeft: '2rem' }}
                                            onClick={() => {
                                                navigator.clipboard.writeText(url);
                                                toast.info(`URL copied: ${url}`);
                                            }}
                                        >
                                            <ContentCopyIcon color="primary" />
                                        </IconButton>
                                    </Box>
                                    <a
                                        href={url}
                                        target="_blank"
                                        style={{ overflowWrap: 'break-word' }}
                                    >
                                        <Typography textOverflow={'clip'}>{url}</Typography>
                                    </a>
                                </Stack>
                            </Tooltip>
                        ) : (
                            <UserPreview />
                        )}
                    </DialogContent>
                </Dialog>
            </form>
        </FormProvider>
    );
}

const debouncedGetUserByEmail = debounce(getUserByEmail, 750, { leading: true, trailing: true });

function UserPreview() {
    const { watch, setValue } = useFormContext<SpoofUserForm>();
    const email = watch('email');
    const [lastEmail, setLastEmail] = useState<string | null>(null);
    const [user, setUser] = useState<User | null | undefined>(null);

    useEffect(() => {
        if (!email) return;
        if (email === lastEmail) return;

        setUser(null);
        setLastEmail(email); // don't fetch again if email hasn't changed

        (async () => {
            try {
                const newUser = await debouncedGetUserByEmail(email);
                setUser(newUser);
            } catch {
                breadcrumb('Failed to fetch user by email', { email });
                setUser(null);
            }
        })();
    }, [email, lastEmail]);

    // Keep form state updated with user status
    useEffect(() => {
        setValue('existingUser', !!user);
    }, [user, setValue]);

    if (!email) return null;

    if (user === null) {
        return (
            <Stack flexDirection={'row'} padding={2} justifyContent={'center'}>
                <CircularProgress />
            </Stack>
        );
    }

    if (user) {
        return (
            <Alert severity="info">
                <AlertTitle>User found:</AlertTitle>
                {user.firstName} {user.lastName}
            </Alert>
        );
    }

    return (
        <Grid2>
            <Typography variant="h6" sx={{ marginTop: '1rem' }}>
                Not an existing user:
            </Typography>
            <WrestlerNameForm />
        </Grid2>
    );
}

const requiredIfNewUser = Yup.string().test(
    'is new user',
    'Name required for new user',
    function (name) {
        return this.parent.existingUser ? true : !!name;
    },
);

const emailFormSchema: Yup.ObjectSchema<SpoofUserForm> = Yup.object({
    email: Yup.string().required('email is required'),
    firstName: requiredIfNewUser,
    lastName: Yup.string().test('is new user', 'Last Name required for new user', function (name) {
        return this.parent.existingUser ? true : !!name;
    }),
    existingUser: Yup.boolean().required(),
});

function SubmitButton({ onSubmit }: { onSubmit: SubmitHandler<SpoofUserForm> }) {
    const { errors, dirtyFields } = useFormState<SpoofUserForm>();
    const { handleSubmit, watch } = useFormContext<SpoofUserForm>();
    const existingUser = watch('existingUser');

    const error = errors?.email?.message || errors?.firstName?.message || errors?.lastName?.message;

    return (
        <Tooltip title={error || 'Spoof user!'}>
            <IconButton
                size="large"
                disabled={
                    !!error ||
                    !dirtyFields.email ||
                    (!existingUser && (!dirtyFields.firstName || !dirtyFields.lastName))
                }
                onClick={() =>
                    handleSubmit(onSubmit, () =>
                        toast.error(`Invalid name, please provide your full name`),
                    )()
                }
            >
                <AutoFixHighIcon color={error ? 'error' : 'primary'} />
            </IconButton>
        </Tooltip>
    );
}
